/*!
 * kb-styling-concierge.js — v9.8.3
 * Implements CONCIERGE_DEV_SPEC "Build Spec v3.1".
 * ---------------------------------------------------------------------------
 * Model:
 *   Q1 role  -> captured for data only. Changes NOTHING the customer sees.
 *   Q2 task  -> same 5 options, same order, same headline, for every role.
 *   Result   -> headline + up to 5 buttons, resolved from the destinations map.
 *   "Talk to a stylist" has no result — it opens Tidio.
 *
 * Changed in v9.7.1, on Jimmy's instruction (these differ from the doc):
 *   - the "Something else / just looking" role was removed, leaving 4
 *   - "Change my answers" was removed; Back is the only footer control
 *   - every role, task and button now carries a second line of text
 *
 * Verified against the live store 09SEP2026:
 *   - all 13 destination URLs return 200
 *   - /products/suit-fabric-swatches is $2.00 (never call it free)
 *   - window.tidioChatApi.popUpOpen() exists
 *   - window.dataLayer + Elevar are present, so events go to dataLayer
 *
 * Config precedence: section "Advanced config JSON" > section settings > BUILT_IN.
 * Invalid JSON in that field is ignored and BUILT_IN is used instead, with a
 * warning surfaced in the theme editor only.
 */
(function () {
  'use strict';

  var STORE_KEY = 'kb_concierge';
  var SESSION_SHOWN = 'kb_concierge_shown';
  var SESSION_TEASER = 'kb_concierge_teaser_closed';

  /* =====================================================================
     BUILT-IN CONFIG — Build Spec v3
     ===================================================================== */
  var BUILT_IN = {
    version: '3.1',
    copy: {
      // v9.8.3: restored from mockup v5, which had TWO lines on each question
      // screen — `bigHeadline` (.m-big, 25px Playfair) above `roleQuestion`
      // (.m-question, 15px grey). The theme build collapsed them into one and
      // the intro line was lost. Blank this out and the question is promoted
      // back to being the only heading.
      headline: 'Let us help you personalize your shopping experience',
      q1: 'What is your role in the wedding?',
      q2: 'What can we help with?',
      launcher_idle: 'Help me find my dress',
      launcher_saved: '{role} · {task}'
    },
    limits: { max_options_per_screen: 5, max_ctas_per_result: 5 },

    // Generated from rules/kb-concierge-content.json — the same file that
    // generates the pre-filled theme-editor blocks, so the two cannot drift.
    // Every `sub` ships blank: fill one in the customizer and a second line
    // appears on that option or button, leave it blank and there is none.
    roles: [
      { key: "bride", label: "Bride", sub: "" },
      { key: "bridesmaid", label: "Bridesmaid", sub: "" },
      { key: "mom", label: "Mother of the Bride or Groom", sub: "" },
      { key: "groom", label: "Groom or Groomsman", sub: "" }
    ],

    // Same five options, same order, same headline, for every role.
    tasks: [
      { key: "start", label: "Just getting started", sub: "",
        headline: "Start here.",
        ctas: ["swatch", "shadefinder", "quiz", "showroom", "guide"] },
      { key: "colors", label: "Find my wedding colors", sub: "",
        headline: "Find your wedding colors.",
        ctas: ["swatch", "shadefinder", "tryon"] },
      { key: "dresses", label: "Shop bridesmaid dresses", sub: "",
        headline: "Time to pick the dress.",
        ctas: ["shop", "instock", "quiz", "tryon", "sizes"] },
      { key: "suits", label: "Color-coordinating suits & accessories", sub: "",
        headline: "Suits and ties, sorted.",
        ctas: ["suitsw", "suits", "ties", "suitcolor"] },
      { key: "chat", label: "Talk to a stylist", sub: "",
        action: { type: 'tidio' } }
    ],

    // All 13 URLs verified 200 on the live store, 09SEP2026.
    destinations: {
      quiz: { label: "Take the 2-minute style quiz", sub: "", url: "/pages/bridesmaid-dress-quiz" },
      swatch: { label: "Order free color swatches", sub: "", url: "/products/free-color-swatches" },
      showroom: { label: "Build a showroom for your party", sub: "", url: "/pages/showroom" },
      shadefinder: { label: "Find your wedding colors", sub: "", url: "/pages/shop-by-color" },
      tryon: { label: "Try dresses on at home", sub: "", url: "/collections/sample-dresses" },
      shop: { label: "Shop bridesmaid dresses", sub: "", url: "/collections/bridesmaid-dresses" },
      instock: { label: "Need it fast? Ready-to-ship dresses", sub: "", url: "/collections/in-stock" },
      suitsw: { label: "Order suit swatches", sub: "", url: "/products/suit-fabric-swatches" },
      suits: { label: "Shop coordinating suits", sub: "", url: "/collections/men-and-womens-suits" },
      ties: { label: "Shop color-coordinating accessories", sub: "", url: "/collections/mens" },
      suitcolor: { label: "Suit & dress color combinations", sub: "", url: "/pages/suit-colors-for-bridesmaid-dresses" },
      guide: { label: "Start-here guide", sub: "", url: "/pages/getting-started" },
      sizes: { label: "Size and measurement guide", sub: "", url: "/pages/measurementguide" }
    },
    landing_hints: {
      '/collections/men*': 'groom',
      '/collections/*suit*': 'groom',
      '/products/suit-fabric-swatches': 'groom',
      '/pages/suit*': 'groom',
      '/pages/generation-tux*': 'groom',
      '/pages/groomsmen': 'groom',
      '/collections/mother-of-the-bride-groom-dresses': 'mom',
      '/pages/mothers-*': 'mom'
    },
    triggers: {
      // v9.8.2: '*/products/*', NOT '/products/*'. Shopify serves every product at
      // both /products/<handle> and /collections/<x>/products/<handle>, and Boost
      // renders the collection-prefixed form — measured 100 of 126 product links on
      // /collections/bridesmaid-dresses. An anchored '/products/*' missed all of them,
      // so the widget was still opening on most real product-page views.
      never_on: ['*/products/*', '/cart', '/checkouts/*', '/account*', '/apps/tracktor/*',
                 '/pages/giveaway*', '/pages/survey*', '/pages/*thank-you*'],
      delay_seconds: 15,
      scroll_percent: 40,
      exit_intent: true,
      max_per_session: 1,
      suppress_if_saved_profile: true,
      persist_days: 75
    }
  };

  /* ===================== boot ===================== */
  var root = document.getElementById('kbsc-root');
  if (!root) return;

  var RAW = {};
  try {
    var tag = document.getElementById('kbsc-config');
    RAW = tag ? (JSON.parse(tag.textContent || tag.innerHTML) || {}) : {};
  } catch (e) { RAW = {}; }

  // Config precedence, highest first:
  //   1. Advanced config JSON  (whole object at once, power-user escape hatch)
  //   2. Theme-editor blocks   (per list — roles, tasks, destinations)
  //   3. BUILT_IN
  // Each list falls back independently, so filling in only Destinations keeps
  // the built-in roles and tasks.
  var CFG = BUILT_IN, cfgError = '';
  if (RAW.configJson) {
    try {
      var over = JSON.parse(RAW.configJson);
      if (over && over.roles && over.tasks && over.destinations) { CFG = over; }
      else { cfgError = 'Advanced config JSON parsed but is missing roles / tasks / destinations. Built-in config used.'; }
    } catch (e2) {
      cfgError = 'Advanced config JSON is not valid JSON (' + e2.message + '). Built-in config used.';
    }
  } else if (RAW.blocks) {
    var b = RAW.blocks, merged = {}, k;
    for (k in BUILT_IN) { if (Object.prototype.hasOwnProperty.call(BUILT_IN, k)) merged[k] = BUILT_IN[k]; }
    if (b.roles && b.roles.length) merged.roles = b.roles;
    if (b.tasks && b.tasks.length) merged.tasks = b.tasks;
    if (b.destinations && Object.keys(b.destinations).length) merged.destinations = b.destinations;
    CFG = merged;
  }

  var S = RAW.settings || {};
  var COPY = CFG.copy || {};
  var TRIG = CFG.triggers || {};
  // Section settings win over config copy where the merchant has set them.
  if (S.q1) COPY.q1 = S.q1;
  // v9.8.3 bugfix: S.q2 was never read, so editing "Question 2" in the
  // customizer silently did nothing — it always fell back to BUILT_IN.
  if (S.q2) COPY.q2 = S.q2;
  // headline is intentionally NOT guarded by a truthiness check: an empty
  // string is a real choice here (hide the intro line), not "unset".
  if (typeof S.headline === 'string') COPY.headline = S.headline;
  if (S.launcherIdle) COPY.launcher_idle = S.launcherIdle;
  var DELAY  = S.delay != null ? parseInt(S.delay, 10) : (TRIG.delay_seconds || 15);
  var SCROLL = S.scrollPct != null ? parseInt(S.scrollPct, 10) : (TRIG.scroll_percent || 40);
  var PERSIST_DAYS = TRIG.persist_days || 75;
  var MAX_CTAS = (CFG.limits && CFG.limits.max_ctas_per_result) || 4;
  var MAX_OPTS = (CFG.limits && CFG.limits.max_options_per_screen) || 5;

  var elLauncher = document.getElementById('kbsc-teaser');
  var elLaunchBtn = document.getElementById('kbsc-teaser-open');
  var elOverlay  = document.getElementById('kbsc-overlay');
  var elModal    = document.getElementById('kbsc-modal');
  var elBody     = document.getElementById('kbsc-body');
  if (!elLauncher || !elOverlay || !elBody) return;

  var state = { role: null, task: null, step: 'closed', autofilled: false };
  var autoOpenFired = false;
  var lastFocus = null;

  var designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ===================== small helpers ===================== */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function safeUrl(u) {
    var v = String(u == null ? '' : u).trim();
    if (!v) return '';
    if (v.charAt(0) === '/' || v.charAt(0) === '#') return v;
    if (/^https?:\/\//i.test(v)) return v;
    return '';
  }
  // Glob match used by never_on and landing_hints. Only '*' is special.
  function globMatch(pattern, path) {
    var rx = '^' + String(pattern).replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$';
    return new RegExp(rx).test(path);
  }
  function currentPath() { return (window.location.pathname || '/').replace(/\/+$/, '') || '/'; }

  /* =====================================================================
     CONSENT (added v9.8.1)
     ---------------------------------------------------------------------
     Consentmo drives Shopify's Customer Privacy API and Google Consent Mode
     on this store (verified 11SEP2026: consent mode defaults every category
     to denied, then updates on the banner choice). Before v9.8.1 this widget
     ignored all of that and wrote its cookie regardless — which contradicted
     the store's own banner.

     What each category gates here:
       preferences -> the 75-day kb_concierge cookie. Denied = the answers
                      live in sessionStorage only, so the widget still works
                      for the visit but leaves nothing behind.
       analytics   -> the concierge_* dataLayer pushes.
       marketing   -> the Klaviyo identify call.

     If the API is present we use it and fail CLOSED until it says yes. If
     window.Shopify does not exist at all (non-Shopify context, e.g. a test
     harness) there is no consent framework to respect, so we proceed.
     ===================================================================== */
  function consentState() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.preferencesProcessingAllowed === 'function') {
      try {
        return {
          known: true,
          preferences: !!cp.preferencesProcessingAllowed(),
          analytics: !!cp.analyticsProcessingAllowed(),
          marketing: !!cp.marketingAllowed()
        };
      } catch (e) {}
    }
    if (!window.Shopify) {
      // No Shopify, no CMP governing this page.
      return { known: false, preferences: true, analytics: true, marketing: true };
    }
    // Shopify is here but the privacy API has not loaded yet — assume nothing.
    return { known: false, preferences: false, analytics: false, marketing: false };
  }
  function mayPersist()  { return consentState().preferences; }
  function mayTrack()    { return consentState().analytics; }
  function mayMarket()   { return consentState().marketing; }

  /* ===================== storage: cookie first, localStorage fallback ===================== */
  function writeCookie(name, value, days) {
    try {
      var d = new Date(); d.setTime(d.getTime() + days * 86400000);
      document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
      return true;
    } catch (e) { return false; }
  }
  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  // Persist to the cookie only with preferences consent. Without it the answer
  // still works for this visit via sessionStorage, and is upgraded to the
  // cookie automatically if the visitor later accepts.
  function saveProfile(obj) {
    var s = JSON.stringify(obj);
    try { sessionStorage.setItem(STORE_KEY, s); } catch (e) {}
    if (!mayPersist()) return;
    writeCookie(STORE_KEY, s, PERSIST_DAYS);
    try { localStorage.setItem(STORE_KEY, s); } catch (e) {}
  }
  function loadProfile() {
    var raw = readCookie(STORE_KEY);
    if (!raw) { try { raw = localStorage.getItem(STORE_KEY); } catch (e) { raw = null; } }
    if (!raw) { try { raw = sessionStorage.getItem(STORE_KEY); } catch (e) { raw = null; } }
    if (!raw) return null;
    try {
      var p = JSON.parse(raw);
      if (!p || !p.role || !p.task) return null;
      if (p.ts && (Date.now() - p.ts) > PERSIST_DAYS * 86400000) { clearProfile(); return null; }
      return p;
    } catch (e) { return null; }
  }
  function clearProfile() {
    writeCookie(STORE_KEY, '', -1);
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  }
  // Drops the persistent copies but keeps the session copy, so withdrawing
  // consent removes the cookie without breaking the visit in progress.
  function dropPersistentCopies() {
    writeCookie(STORE_KEY, '', -1);
    try { localStorage.removeItem(STORE_KEY); } catch (e) {}
    try { localStorage.removeItem('kb_pending_concierge_props'); } catch (e) {}
  }
  function sessionFlag(key, set) {
    try {
      if (set) { sessionStorage.setItem(key, '1'); return true; }
      return sessionStorage.getItem(key) === '1';
    } catch (e) { return false; }
  }

  /* ===================== analytics + klaviyo ===================== */
  function track(name, props) {
    if (!mayTrack()) return;                       // analytics consent required
    var payload = props || {};
    payload.event = name;
    try { window.dataLayer = window.dataLayer || []; window.dataLayer.push(payload); } catch (e) {}
    if (designMode && window.console) console.log('[concierge]', name, props || {});
  }
  function klaviyoIdentify(props) {
    if (!mayMarket()) return false;                // marketing consent required
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify(props); return true; } catch (e) {}
    }
    // No email known yet — stash so it can ride along with the next identify.
    // Only persisted with preferences consent; otherwise it is dropped.
    if (mayPersist()) {
      try { localStorage.setItem('kb_pending_concierge_props', JSON.stringify(props)); } catch (e) {}
    }
    return false;
  }
  function flushPendingKlaviyo() {
    if (!mayMarket()) return;
    if (!(window.klaviyo && typeof window.klaviyo.identify === 'function')) return;
    try {
      var raw = localStorage.getItem('kb_pending_concierge_props');
      if (!raw) return;
      window.klaviyo.identify(JSON.parse(raw));
      localStorage.removeItem('kb_pending_concierge_props');
    } catch (e) {}
  }

  // Re-evaluate when the shopper answers the Consentmo banner. Accepting
  // upgrades the session answer to the cookie; declining removes it.
  var lastConsent = consentState();
  function onConsentChange() {
    var now = consentState();
    if (now.preferences && !lastConsent.preferences) {
      try {
        var s = sessionStorage.getItem(STORE_KEY);
        if (s) { writeCookie(STORE_KEY, s, PERSIST_DAYS); localStorage.setItem(STORE_KEY, s); }
      } catch (e) {}
    } else if (!now.preferences && lastConsent.preferences) {
      dropPersistentCopies();
    }
    if (now.marketing && !lastConsent.marketing) flushPendingKlaviyo();
    lastConsent = now;
  }
  document.addEventListener('visitorConsentCollected', onConsentChange);
  document.addEventListener('trackingConsentAccepted', onConsentChange);

  /* ===================== config lookups ===================== */
  function roleByKey(k) {
    for (var i = 0; i < CFG.roles.length; i++) if (CFG.roles[i].key === k) return CFG.roles[i];
    return null;
  }
  function taskByKey(k) {
    for (var i = 0; i < CFG.tasks.length; i++) if (CFG.tasks[i].key === k) return CFG.tasks[i];
    return null;
  }
  // v3.1: identical for every role — role no longer reorders anything.
  function tasksForRole() {
    return CFG.tasks.slice(0, MAX_OPTS);
  }
  function ctasFor(roleKey, taskKey) {
    var task = taskByKey(taskKey);
    if (!task || !task.ctas) return [];
    var keys = task.ctas.slice();
    var out = [];
    for (var k = 0; k < keys.length && out.length < MAX_CTAS; k++) {
      var d = CFG.destinations[keys[k]];
      if (!d || !safeUrl(d.url)) continue;
      out.push({ key: keys[k], label: d.label, sub: d.sub, url: safeUrl(d.url) });
    }
    return out;
  }

  /* ===================== rendering ===================== */
  function optionsHtml(items, attr) {
    var out = '';
    for (var i = 0; i < items.length && i < MAX_OPTS; i++) {
      var it = items[i];
      out += '<button type="button" class="kbsc__opt" ' + attr + '="' + esc(it.key) + '">' +
             '<span class="kbsc__opt-label">' + esc(it.label) + '</span>' +
             (it.sub ? '<span class="kbsc__opt-sub">' + esc(it.sub) + '</span>' : '') +
             '</button>';
    }
    return '<div class="kbsc__optlist">' + out + '</div>';
  }
  // Every button is styled identically — no filled "primary" first button.
  function ctasHtml(list) {
    var out = '';
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      out += '<a class="kbsc__cta"' +
             ' href="' + esc(c.url) + '" data-kbsc-cta="' + esc(c.key) + '" data-kbsc-pos="' + (i + 1) + '">' +
             '<span class="kbsc__cta-lbl">' + esc(c.label) + '</span>' +
             (c.sub ? '<span class="kbsc__cta-desc">' + esc(c.sub) + '</span>' : '') +
             '</a>';
    }
    return '<div class="kbsc__ctalist">' + out + '</div>';
  }
  // Back only. "Change my answers" was removed on Jimmy's instruction —
  // Back from the result already returns to Q2, and Back from Q2 to Q1.
  function footerHtml(backTarget) {
    return '<div class="kbsc__footer">' +
      '<button type="button" class="kbsc__back" data-kbsc-back="' + backTarget + '">&lsaquo; Back</button>' +
      '</div>';
  }
  // Two lines on the question screens, restored from mockup v5:
  //   .kbsc__big  intro line, Playfair, large      (section setting "Intro line")
  //   .kbsc__q    the question, body font, grey    (section settings Question 1 / 2)
  // Leave the intro line blank in the customizer and the question is promoted
  // back to being the sole heading, which is exactly how v9.8.2 looked.
  function headHtml(question) {
    var intro = COPY.headline == null ? '' : String(COPY.headline).trim();
    if (!intro) {
      return '<h2 class="kbsc__big" id="kbsc-title">' + esc(question) + '</h2>';
    }
    return '<h2 class="kbsc__big kbsc__big--tight" id="kbsc-title">' + esc(intro) + '</h2>' +
           '<p class="kbsc__q" id="kbsc-subtitle">' + esc(question) + '</p>';
  }
  function editorNotice() {
    if (!designMode || !cfgError) return '';
    return '<div class="kbsc__warn"><b>Theme editor notice</b><br>' + esc(cfgError) + '</div>';
  }

  function render() {
    if (state.step === 'closed') {
      elOverlay.hidden = true;
      elLauncher.hidden = launcherShouldHide();
      return;
    }
    elOverlay.hidden = false;
    elLauncher.hidden = true; // spec: hide the launcher while the modal is open

    var html = editorNotice();
    if (state.step === 'role') {
      html += headHtml(COPY.q1) +
              optionsHtml(CFG.roles, 'data-kbsc-role') +
              footerHtml('close');
    } else if (state.step === 'task') {
      // Same headline and same options for every role (spec v3.1).
      html += headHtml(COPY.q2 || 'What can we help with?') +
              optionsHtml(tasksForRole(), 'data-kbsc-task') +
              // When the role was auto-filled from the URL there is no Q1 to
              // go back to, so Back closes instead of showing an empty step.
              footerHtml(state.autofilled ? 'close' : 'role');
    } else if (state.step === 'result') {
      var t = taskByKey(state.task);
      html += '<h2 class="kbsc__result-head" id="kbsc-title">' + esc(t ? t.headline : '') + '</h2>' +
              ctasHtml(ctasFor(state.role, state.task)) +
              footerHtml('task');
    }
    elBody.innerHTML = html;

    // The dialog is labelled by the intro line; when a question is rendered as
    // a separate second line, point aria-describedby at it so a screen reader
    // announces both. Removed again on the result screen, which has one line.
    if (elBody.querySelector('#kbsc-subtitle')) elModal.setAttribute('aria-describedby', 'kbsc-subtitle');
    else elModal.removeAttribute('aria-describedby');

    var first = elBody.querySelector('.kbsc__opt, .kbsc__cta');
    if (first) { try { first.focus({ preventScroll: true }); } catch (e) {} }
  }

  function launcherShouldHide() {
    if (sessionFlag(SESSION_TEASER, false)) return true;
    return false;
  }
  // The launcher text never changes. The spec had it switch to "Bride · Shop
  // dresses" once answers were saved; removed on request.
  function syncLauncher() {
    if (elLaunchBtn) elLaunchBtn.textContent = COPY.launcher_idle || 'Help me find my dress';
    elLauncher.hidden = launcherShouldHide();
  }

  /* ===================== flow ===================== */
  function openModal(source) {
    lastFocus = document.activeElement;
    var p = loadProfile();
    if (p) {
      state.role = p.role; state.task = p.task; state.step = 'result';
      track('concierge_returning', {
        role: p.role, task: p.task,
        days_since: p.ts ? Math.round((Date.now() - p.ts) / 86400000) : null
      });
    } else {
      var hinted = landingHintRole();
      if (hinted) {
        state.role = hinted; state.autofilled = true; state.step = 'task';
        track('concierge_role', { role: hinted, autofilled: true });
      } else {
        state.step = 'role';
      }
    }
    track('concierge_shown', { trigger: source, page_path: currentPath() });
    sessionFlag(SESSION_SHOWN, true);
    render();
  }
  function closeModal(reason) {
    if (reason) track('concierge_dismissed', { step: state.step });
    state.step = 'closed';
    render();
    syncLauncher();
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus({ preventScroll: true }); } catch (e) {} }
  }
  function landingHintRole() {
    var path = currentPath(), hints = CFG.landing_hints || {};
    for (var pattern in hints) {
      if (Object.prototype.hasOwnProperty.call(hints, pattern) && globMatch(pattern, path)) return hints[pattern];
    }
    return null;
  }
  function chooseRole(key) {
    state.role = key; state.autofilled = false;
    track('concierge_role', { role: key, autofilled: false });
    state.step = 'task';
    render();
  }
  function chooseTask(key) {
    var t = taskByKey(key);
    state.task = key;
    track('concierge_task', { role: state.role, task: key });

    if (t && t.action && t.action.type === 'tidio') {
      openTidio();
      return;
    }
    persistAndShowResult();
  }
  function persistAndShowResult() {
    saveProfile({ role: state.role, task: state.task, ts: Date.now() });
    var role = roleByKey(state.role);
    var props = {
      concierge_task: state.task,
      concierge_last_seen: new Date().toISOString().slice(0, 10)
    };
    // spec: do not send "other" as a role
    if (!role || role.send_to_klaviyo !== false) props.concierge_role = state.role;
    klaviyoIdentify(props);
    track('concierge_result', { role: state.role, task: state.task });
    state.step = 'result';
    render();
    syncLauncher();
  }
  function openTidio() {
    track('concierge_chat', { role: state.role });
    // Pass the role through so the agent sees who they are talking to.
    try {
      if (window.tidioChatApi && state.role && typeof window.tidioChatApi.addVisitorTags === 'function') {
        window.tidioChatApi.addVisitorTags(['concierge:' + state.role]);
      }
    } catch (e) {}
    closeModal(false);          // spec: close the modal first
    var tries = 0;
    (function attempt() {
      if (window.tidioChatApi && typeof window.tidioChatApi.popUpOpen === 'function') {
        try { window.tidioChatApi.popUpOpen(); return; } catch (e) {}
      }
      if (window.tidioChatApi && typeof window.tidioChatApi.open === 'function') {
        try { window.tidioChatApi.open(); return; } catch (e) {}
      }
      if (++tries < 20) setTimeout(attempt, 250);
    })();
  }

  /* ===================== triggers ===================== */
  function pageBlocked() {
    var path = currentPath(), list = (TRIG.never_on || []);
    for (var i = 0; i < list.length; i++) if (globMatch(list[i], path)) return true;
    return false;
  }
  function mayAutoOpen() {
    if (autoOpenFired) return false;
    if (S.trigger === 'manual') return false;
    if (pageBlocked()) return false;
    if (sessionFlag(SESSION_SHOWN, false)) return false;          // once per session
    if (sessionFlag(SESSION_TEASER, false)) return false;         // launcher dismissed
    if (TRIG.suppress_if_saved_profile !== false && loadProfile()) return false; // returning visitor
    return true;
  }
  function fireAuto(source) {
    if (!mayAutoOpen()) return;
    autoOpenFired = true;
    openModal(source);
  }
  function armTriggers() {
    if (!mayAutoOpen()) return;
    setTimeout(function () { fireAuto('time'); }, Math.max(0, DELAY) * 1000);
    window.addEventListener('scroll', function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.pageYOffset / h) * 100 >= SCROLL) fireAuto('scroll');
    }, { passive: true });
    if (TRIG.exit_intent !== false) {
      document.addEventListener('mouseout', function (ev) {
        if (!ev.relatedTarget && ev.clientY <= 0) fireAuto('exit');
      });
    }
  }

  /* ===================== events ===================== */
  function closest(t, sel) { return t && t.closest ? t.closest(sel) : null; }

  document.addEventListener('click', function (ev) {
    var t = ev.target, el;

    if (closest(t, '#kbsc-teaser-open')) { openModal('launcher'); return; }
    if (closest(t, '#kbsc-teaser-close')) {
      sessionFlag(SESSION_TEASER, true);
      track('concierge_dismissed', { step: 'launcher' });
      syncLauncher();
      return;
    }
    if (closest(t, '#kbsc-close') || t === elOverlay) { closeModal(true); return; }

    if ((el = closest(t, '[data-kbsc-role]'))) { chooseRole(el.getAttribute('data-kbsc-role')); return; }
    if ((el = closest(t, '[data-kbsc-task]'))) { chooseTask(el.getAttribute('data-kbsc-task')); return; }

    if ((el = closest(t, '[data-kbsc-back]'))) {
      var to = el.getAttribute('data-kbsc-back');
      if (to === 'close') { closeModal(true); }
      else if (to === 'role') { state.step = 'role'; state.autofilled = false; render(); }
      else if (to === 'task') {
        // Going back from a saved result means they want to change it.
        clearProfile();
        state.task = null; state.step = 'task';
        render(); syncLauncher();
      }
      return;
    }
    if ((el = closest(t, '[data-kbsc-cta]'))) {
      track('concierge_cta', {
        role: state.role, task: state.task,
        cta_key: el.getAttribute('data-kbsc-cta'),
        position: parseInt(el.getAttribute('data-kbsc-pos'), 10),
        url: el.getAttribute('href')
      });
      // The click navigates away. The saved profile plus the once-per-session
      // flag stop the modal reopening on the destination page.
      return;
    }
  });

  // Esc closes; Tab is trapped inside the dialog while it is open.
  document.addEventListener('keydown', function (ev) {
    if (state.step === 'closed') return;
    if (ev.key === 'Escape') { closeModal(true); return; }
    if (ev.key !== 'Tab') return;
    var f = elModal.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  });

  /* ===================== init ===================== */
  flushPendingKlaviyo();
  syncLauncher();
  render();
  armTriggers();

  // exposed for QA only
  window.__kbsc = {
    version: '9.8.3',
    cfg: CFG,
    cfgError: cfgError,
    state: state,
    pageBlocked: pageBlocked,
    landingHintRole: landingHintRole,
    tasksForRole: tasksForRole,
    ctasFor: ctasFor,
    loadProfile: loadProfile,
    consentState: consentState,
    mayPersist: mayPersist,
    mayTrack: mayTrack,
    mayMarket: mayMarket,
    clearProfile: clearProfile,
    open: openModal
  };
})();
